import { Loader } from 'src/design/atoms/Loader';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { WorkflowDto } from 'src/api/FiretrolApiClient';
import { useWorkflowTemplate } from 'src/api/queries/workflows/useWorkflowTemplate';
import { CreateWorkflow } from 'src/app/workflows/CreateWorkflow/CreateWorkflow';
import { useJobs } from 'src/hooks/useJobs';
import { Header } from 'src/design/organisms/Header';

export const EditWorkflow: React.FC = () => {
  const params = useParams<{ workflowId: string }>();
  const navigate = useNavigate();
  const { selectedJob } = useJobs();
  const { data, isLoading, isError } = useWorkflowTemplate(
    params.workflowId ? Number(params.workflowId) : null,
  );

  // Opening a template from a job clones it into an unsaved job workflow;
  // id -1 is what the upsert endpoint treats as "create".
  const workflowSettings = React.useMemo<WorkflowDto | null>(() => {
    if (!data || !selectedJob) return null;
    if (!data.isTemplate) return data;
    return {
      ...data,
      id: -1,
      isTemplate: false,
      name: `${data.name} (Copy)`,
      jobId: selectedJob.id,
    };
  }, [data, selectedJob]);

  const onSaveComplete = (workflow?: WorkflowDto) => {
    if (!selectedJob || !workflow) return;
    if (Number(params.workflowId) !== workflow.id) {
      navigate(`/jobs/${selectedJob.id}/workflows/${workflow.id}`);
    }
  };

  if (isLoading) return <Loader />;
  if (isError) return <p>Failed to load workflow.</p>;
  if (!workflowSettings) return <p>No Workflow</p>;

  return (
    <>
      <Header
        title={workflowSettings.name}
        breadcrumbProps={{
          items: [
            { text: 'Jobs', href: '/jobs' },
            {
              text: `${selectedJob?.name} - ${selectedJob?.jobNumber}`,
              href: `/jobs/${selectedJob?.id}/summary`,
            },
            {
              text: 'Workflows',
              href: `/jobs/${selectedJob?.id}/workflows`,
            },
            { text: workflowSettings.name },
          ],
        }}
        helpText="Configure the approval steps and notification behavior for this job's workflow."
      />
      <CreateWorkflow
        initialSettings={workflowSettings}
        initialSteps={workflowSettings.steps}
        onSaveComplete={onSaveComplete}
        onNavigate={() => navigate(`/jobs/${selectedJob?.id}/workflows`)}
      />
    </>
  );
};
